<template>
    <FormStepLayout>
        <template v-if="! invitation || (invitation && ! invitation?.organization_id)" #progress-bar>
            <template v-if="invitation">
                <form-progress-bar :steps="2" :current-step="1"></form-progress-bar>
            </template>
            <template v-else>
                <form-progress-bar :steps="3" :current-step="2"></form-progress-bar>
            </template>
        </template>
        <template #title>
            <template v-if="invitation && invitation.organization_id">
                {{ $t('sign_up.sign_up_with_invitation_heading') }}
            </template>
            <template v-else>
                {{ $t('sign_up.account_data_heading') }}
            </template>
        </template>
        <template #info>
            <template v-if="invitation && invitation.organization_id">
                <template v-html="$t('sign_up.sign_up_with_invitation_info', {
                    emphasizedOpen: '<span class=&quot;font-semibold&quot;>',
                    name: `${invitation.sender.first_name} ${invitation.sender.last_name}`,
                    emphasizedClose: '</span>',
                    organization: invitation.organization.name})">
                </template>
            </template>
            <template v-else>
                {{ $t('sign_up.account_data_info') }}
            </template>
        </template>
        <div>
            <form @submit.prevent="onSubmit" @keydown="form.errors.forget($event.target.name)">
                <p v-if="form.errors.has('organization_id') || form.errors.has('organization_name')" class="text-danger-500 mb-3">
                    {{ $t('sign_up.organization_error') }}
                </p>
                <div class="flex flex-row">
                    <div class="flex-1 mb-3 mr-3" :class="{ 'has-error' : form.errors.has('first_name') }">
                        <label class="mb-1" for="first_name">{{ $t('globals.first_name') }}</label>
                        <input class="form-control" id="first_name" name="first_name" type="text" :placeholder="$t('sign_up.first_name_placeholder')" required v-model="form.first_name" />
                        <p class="help-block" v-if="form.errors.has('first_name')" v-text="form.errors.get('first_name')"></p>
                    </div>
                    <div class="flex-1 mb-3" :class="{ 'has-error' : form.errors.has('last_name') }">
                        <label class="mb-1" for="last_name">{{ $t('globals.last_name') }}</label>
                        <input class="form-control" id="last_name" name="last_name" type="text" :placeholder="$t('sign_up.last_name_placeholder')" required v-model="form.last_name" />
                        <p class="help-block" v-if="form.errors.has('last_name')" v-text="form.errors.get('last_name')"></p>
                    </div>
                </div>
                <div class="mb-3" :class="{ 'has-error' : form.errors.has('email') }">
                    <label class="mb-1" for="email">{{ $t('auth.email') }}</label>
                    <input class="form-control" id="email" name="email" type="email" :placeholder="$t('sign_up.email_placeholder')" required :readonly="invitation" v-model="form.email" />
                    <p class="help-block" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></p>
                </div>
                <div class="mb-3" :class="{ 'has-error' : form.errors.has('password') }">
                    <label class="mb-1" for="password">{{ $t('auth.password') }}</label>
                    <div class="input-group">
                        <input class="form-control" id="password" name="password" :placeholder="$t('sign_up.password_placeholder')" :type="passwordFieldType" required v-model="form.password" />
                        <span class="input-group-addon">
                            <label class="custom-control custom-control-blue-500 custom-checkbox">
                                <input class="custom-control-input" type="checkbox" v-model="passwordFieldType" true-value="text" false-value="password" @click="switchPasswordVisibility">
                                <span class="custom-control-indicator"></span>
                                <span class="custom-control-label">{{ $t('auth.show') }}</span>
                            </label>
                        </span>
                    </div>
                    <p class="help-block" v-if="form.errors.has('password')" v-text="form.errors.get('password')"></p>
                </div>
                <div class="mb-3" :class="{ 'has-error' : form.errors.has('user_agreement') }">
                    <label class="custom-control custom-control-blue-500 custom-checkbox">
                        <input @change="form.errors.forget($event.target.name)" class="custom-control-input" name="user_agreement" type="checkbox" v-model="form.user_agreement">
                        <span class="custom-control-indicator"></span>
                        <span class="custom-control-label" v-html="$t('auth.user_agreement_line', {
                            termsLinkOpen: '<a href=&quot;/terms-and-conditions&quot; class=&quot;text-blue-500&quot; target=&quot;_blank&quot;>',
                            privacyLinkOpen: '<a href=&quot;/privacy-policy&quot; class=&quot;text-blue-500&quot; target=&quot;_blank&quot;>',
                            linkClose: '</a>',
                        })"></span>
                    </label>
                    <p class="help-block" v-if="form.errors.has('user_agreement')" v-text="form.errors.get('user_agreement')"></p>
                </div>
                <div class="pt-2">
                    <button class="btn btn-block btn-primary-blue" :disabled="form.errors.any()" type="submit">{{ $t('auth.sign_up') }}</button>
                </div>
            </form>
            <div v-if="invitation === null || invitation.organization_id === null" class="pt-8">
                <a @click="goBack" class="link-muted hover:text-blue-500 focus:text-blue-500 font-medium text-left no-underline" href="javascript:;">&larr; {{ $t('globals.back') }}</a>
            </div>
        </div>
    </FormStepLayout>
</template>

<script>
import FormStepLayout from './FormStepLayout.vue';
import FormProgressBar from './form-progress-bar.vue';

export default {
    components: {
        FormStepLayout,
        'form-progress-bar': FormProgressBar,
    },

    props: {
        organizationId: {
            type: Number,
            default: null,
        },
        organizationName: {
            type: String,
            default: null,
        },
        creatorPersona: {
            type: String,
            default: null,
        },
        invitation: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            form: new Form({
                first_name: '',
                last_name: '',
                email: this.invitation ? this.invitation.email : '',
                password: '',
                user_agreement: false,
                organization_id: this.organizationId,
                organization_name: this.organizationName,
                creator_persona: this.creatorPersona,
                invitation_token: this.invitation ? this.invitation.token : null,
                refferal_application_id: this.getRefferalApplicationId(),
                refferal_application_user_agreement: true,
            }),
            passwordFieldType: 'password',
        };
    },

    computed: {
        backWithReset() {
            return this.form.errors.has('organization_id')
                || this.form.errors.has('organization_name')
                || this.form.errors.has('creator_persona');
        },
    },

    methods: {
        switchPasswordVisibility() {
            this.passwordFieldType = (this.passwordFieldType === 'password') ? 'text' : 'password';
        },

        onSubmit() {
            Globals.post('/sign-up', this.form)
                .then(() => window.location.href = '/dashboard')
                .catch((error) => {
                    if (error.message === 'Your email address is not verified.') {
                        window.location.href = '/email/verify';
                    } else if (error.message === 'You haven\'t seen the welcome message yet.') {
                        window.location.href = '/welcome';
                    }
                });
        },

        goBack() {
            Bus.$emit('changeScreen', {
                hash: window.location.hash.substring(1),
                step: 2,
                organizationName: !this.backWithReset ? this.organizationName : '',
                creatorPersona: !this.backWithReset ? this.creatorPersona : '',
            });
        },

        getRefferalApplicationId() {
            return 3;
        },
    },
};
</script>
